import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from "@angular/forms";
import { SvgIconComponent } from "../../svg-icon/svg-icon.component";
import {
  DateTimeAdapter,
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
  PickerType
} from "@danielmoncada/angular-datetime-picker";
import { DATE_CONFIG, DEFAULT_DATETIME_TIME, LOCALES } from "../../../constants/app-constants";
import { NgClass } from "@angular/common";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { GridTableService } from "../../../services/grid-table.service";
import { max, min } from "rxjs";

type DateTimeType = "date" | "datetime-local" | "time" | "month";

@Component({
  selector: "app-date-picker",
  standalone: true,
  imports: [SvgIconComponent, OwlDateTimeModule, OwlNativeDateTimeModule, NgClass, FormsModule],
  templateUrl: "./date-picker.component.html",
  styleUrl: "./date-picker.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class DatePickerComponent implements ControlValueAccessor, ICellEditorAngularComp {
  @Input() type: "date" | "datetime-local" | string = "date";
  @Input() placeholder: string = "Select a date";
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() defaultToCurrentDate: boolean = false;

  @Input() maxRangeFromCurrent: number = DATE_CONFIG.maxRangeFromCurrent;
  @Input() minRangeFromCurrent: number = DATE_CONFIG.minRangeFromCurrent;
  @Input() rangeUnit: 'days' | 'weeks' | 'months' | 'years' = DATE_CONFIG.rangeUnit;
  @Input() minRangeUnit: 'days' | 'weeks' | 'months' | 'years' | undefined;
  @Input() maxRangeUnit: 'days' | 'weeks' | 'months' | 'years' | undefined;

  // Computed properties for minDate and maxDate based on range values
  get computedMinDate(): string {
    if (this.minRangeFromCurrent === 0) return '';

    const currentDate = new Date();
    const targetDate = new Date();
    const unit = this.minRangeUnit || this.rangeUnit;

    switch (unit) {
      case 'days':
        targetDate.setDate(currentDate.getDate() + this.minRangeFromCurrent);
        break;
      case 'weeks':
        targetDate.setDate(currentDate.getDate() + (this.minRangeFromCurrent * 7));
        break;
      case 'months':
        targetDate.setMonth(currentDate.getMonth() + this.minRangeFromCurrent);
        break;
      case 'years':
        targetDate.setFullYear(currentDate.getFullYear() + this.minRangeFromCurrent);
        break;
    }

    return this.getDateString(targetDate);
  }

  get computedMaxDate(): string {
    if (this.maxRangeFromCurrent === 0) return '';

    const currentDate = new Date();
    const targetDate = new Date();
    const unit = this.maxRangeUnit || this.rangeUnit;

    switch (unit) {
      case 'days':
        targetDate.setDate(currentDate.getDate() + this.maxRangeFromCurrent);
        break;
      case 'weeks':
        targetDate.setDate(currentDate.getDate() + (this.maxRangeFromCurrent * 7));
        break;
      case 'months':
        targetDate.setMonth(currentDate.getMonth() + this.maxRangeFromCurrent);
        break;
      case 'years':
        targetDate.setFullYear(currentDate.getFullYear() + this.maxRangeFromCurrent);
        break;
    }

    return this.getDateString(targetDate);
  }

  @ViewChild("dateTimePicker") picker!: any;

  ngControl: NgControl | null = null;
  defaultPlaceHolder: string = "Enter Date";
  DateTimeTypesVsPickerType: Record<string, PickerType> = {
    "date": "calendar",
    "datetime-local": "both",
    "time": "timer",
    "month": "calendar"
  };

  @Output() dateChange = new EventEmitter<string>();

  @ViewChild("visibleInput") dateInput !: ElementRef<HTMLInputElement>;

  value: string = "";
  private agGridParams: any;
  // displayValue: string = '';

  onChange: any = () => {
  };
  onTouched: any = () => {
  };


  constructor(private dateTimeAdapter: DateTimeAdapter<any>,
              private injector: Injector,
              private gridTableService: GridTableService) {
    this.dateTimeAdapter.setLocale(navigator.language || LOCALES.EN_IN.value);
  }

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string): void {
    if (this.defaultToCurrentDate && !value) {
      const currentDate = this.getDateString(new Date());
      this.value = currentDate;
      // this.displayValue = this.formatDisplayValue(currentDate);
      setTimeout(() => {
        this.onChange(currentDate);
        this.onTouched();
        this.dateChange.emit(currentDate);
      });
    } else {
      const parsedDate = this.parseDateValue(value);
      if (!parsedDate) {
        this.value = "";
        return;
      }
      this.value = this.getDateString(parsedDate);
      // this.displayValue = this.formatDisplayValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(date: any): void {
    this.value = date.value ? this.getDateString(date.value) : "";
    // this.displayValue = this.formatDisplayValue(this.value);
    this.validateNow()
    this.onChange(this.value);
    this.onTouched();
    this.dateChange.emit(this.value);
    this.gridTableService.commitAndRefocusCell(this.agGridParams);
  }

  //TODO: Remove post discussion
  private formatDisplayValue(value: string): string {
    if (!value) return "";

    const date = new Date(value);
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "numeric",
      year: "numeric"
    };

    if (this.type === "date") {
      return date.toLocaleDateString([], dateOptions);
    } else {
      const datePart = date.toLocaleDateString([], dateOptions);
      const timePart = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
      }).toUpperCase(); // Convert entire time string to uppercase

      return `${datePart}, ${timePart}`;
    }
  }

  private getDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    switch (this.type) {
      case "date":
        return `${year}-${month}-${day}`;
      case "datetime-local": {
        // get timezone offset in minutes
        const offset = -date.getTimezoneOffset();
        const sign = offset >= 0 ? "+" : "-";
        const absOffset = Math.abs(offset);
        const offsetHours = Math.floor(absOffset / 60)
          .toString()
          .padStart(2, "0");
        const offsetMinutes = (absOffset % 60).toString().padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
      }
      case "time":
        return `${hours}:${minutes}`;
      case "month":
        return `${year}-${month}`;
      default:
        return "";
    }
  }

  private parseDateValue(value: string | number): Date | null {
    if (!value) return null;
    if (typeof value === "number") {
      return new Date(value);
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl?.control?.touched && this.ngControl?.control?.errors;
  }

  openOnFocus() {
    if (this.disabled || this.readonly) return;
    if (!this.picker?.opened) {
      this.picker.open();
    }
  }

  validateNow(){
    this.agGridParams?.validate?.();
  }

  agInit(params: any): void {
    this.agGridParams = params;
    Object.keys(params).forEach((k: string) => {
      if ((this as any).hasOwnProperty(k)) {
        (this as any)[k] = (params as any)[k];
      }
    });

    this.writeValue(params.value);
    this.dateTimeAdapter.setLocale(navigator.language ?? LOCALES.EN_IN.value);
  }

  getValue(): any {
    return this.value;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  afterGuiAttached() {
    this.openOnFocus()
    setTimeout(() => {
      this.dateInput.nativeElement.focus();
      this.dateInput.nativeElement.select();
    }, 0);
  }

  getValidationElement(): HTMLElement {
    return this.dateInput.nativeElement;
  }

  getValidationErrors(): string[] | null {
    return this.gridTableService.validateEditableGrid(this.agGridParams.col, this.value);
  }

  get defaultStartAt(): Date | null {
    // For datetime-local, return a date with default time (8:00 AM)
    if (this.type === "datetime-local") {
      const today = new Date();
      today.setHours(DEFAULT_DATETIME_TIME.hour, DEFAULT_DATETIME_TIME.minute, DEFAULT_DATETIME_TIME.second, 0);
      return today;
    }
    return null;
  }

  public dateRangeFilter = (d: Date): boolean => {
    const targetDate = new Date(d);
    const currentDate = new Date();

    // Calculate the actual minDate using minRangeUnit
    let minDate: Date | null = null;
    if (this.minRangeFromCurrent !== 0) {
      minDate = new Date();
      const minUnit = this.minRangeUnit || this.rangeUnit;
      switch (minUnit) {
        case 'days':
          minDate.setDate(currentDate.getDate() + this.minRangeFromCurrent);
          break;
        case 'weeks':
          minDate.setDate(currentDate.getDate() + (this.minRangeFromCurrent * 7));
          break;
        case 'months':
          minDate.setMonth(currentDate.getMonth() + this.minRangeFromCurrent);
          break;
        case 'years':
          minDate.setFullYear(currentDate.getFullYear() + this.minRangeFromCurrent);
          break;
      }
    }

    // Calculate the actual maxDate using maxRangeUnit
    let maxDate: Date | null = null;
    if (this.maxRangeFromCurrent !== 0) {
      maxDate = new Date();
      const maxUnit = this.maxRangeUnit || this.rangeUnit;
      switch (maxUnit) {
        case 'days':
          maxDate.setDate(currentDate.getDate() + this.maxRangeFromCurrent);
          break;
        case 'weeks':
          maxDate.setDate(currentDate.getDate() + (this.maxRangeFromCurrent * 7));
          break;
        case 'months':
          maxDate.setMonth(currentDate.getMonth() + this.maxRangeFromCurrent);
          break;
        case 'years':
          maxDate.setFullYear(currentDate.getFullYear() + this.maxRangeFromCurrent);
          break;
      }
    }

    // Check if the date is within the allowed range
    const isAfterMin = minDate === null || targetDate >= minDate;
    const isBeforeMax = maxDate === null || targetDate <= maxDate;
    return isAfterMin && isBeforeMax;
  }
  protected readonly min = min;
  protected readonly max = max;
}
